import React, { useMemo } from 'react';
import { View, Text, Image, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Paprika } from '../../../../styles/theme';
import { useTranslation } from 'react-i18next';
import { formatRating } from '../../../../utils/formatNumber';
import { getSourceFaviconUrl, getSourceDisplayName } from '../../../../utils/recipeSource';

interface Props {
  sourceUrl?: string | null;
  rating?: number | null;
  /** Total cook time in minutes. */
  minutes?: number | null;
}

function formatMinutes(m: number): string {
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h${m % 60 ? String(m % 60).padStart(2, '0') : ''}`;
}

/**
 * Single-line card meta row: a compact source+rating pill on the left (favicon +
 * star + rating + source name, name truncates) and the total-time pill on the
 * right. One row so every card aligns identically whether or not it has a rating,
 * and the time is never pushed off-card. Spec: design image `rATqbo`.
 */
export function CardMeta({ sourceUrl, rating, minutes }: Props) {
  const { i18n } = useTranslation();
  const favicon = useMemo(() => (sourceUrl ? getSourceFaviconUrl(sourceUrl) : null), [sourceUrl]);
  const name = useMemo(() => (sourceUrl ? getSourceDisplayName(sourceUrl) : ''), [sourceUrl]);
  const hasRating = rating != null && rating > 0;
  const hasTime = minutes != null && minutes > 0;

  return (
    <View style={styles.row}>
      {sourceUrl ? (
        <View style={styles.sourcePill}>
          {favicon ? <Image source={{ uri: favicon }} style={styles.favicon} resizeMode="contain" /> : null}
          {hasRating ? (
            <>
              <Ionicons name="star" size={11} color="#F5A623" style={styles.star} />
              <Text style={styles.rating}>{formatRating(rating, i18n.language)}</Text>
            </>
          ) : null}
          <Text style={styles.source} numberOfLines={1}>{name}</Text>
        </View>
      ) : null}

      {hasTime ? (
        <View style={styles.timePill}>
          <Ionicons name="time-outline" size={11} color={Paprika.textSecondary} style={styles.clock} />
          <Text style={styles.time}>{formatMinutes(minutes)}</Text>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  sourcePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    backgroundColor: Paprika.surfaceLow,
    borderRadius: 6,
    paddingHorizontal: 7,
    paddingVertical: 3,
    flexShrink: 1,
  },
  favicon: {
    width: 13,
    height: 13,
    borderRadius: 3,
    flexShrink: 0,
  },
  star: {
    flexShrink: 0,
    marginLeft: 1,
  },
  rating: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 11,
    color: Paprika.textPrimary,
    flexShrink: 0,
  },
  source: {
    fontFamily: 'Inter-Medium',
    fontSize: 11,
    color: Paprika.textSecondary,
    flexShrink: 1,
  },
  timePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    backgroundColor: Paprika.bgSunk,
    borderRadius: 6,
    paddingHorizontal: 7,
    paddingVertical: 3,
    flexShrink: 0,
    marginLeft: 'auto' as any,
  },
  clock: {
    flexShrink: 0,
  },
  time: {
    fontFamily: 'Inter-Medium',
    fontSize: 11,
    color: Paprika.textSecondary,
  },
});
